import React, { useEffect, useState, useRef } from 'react';
import MedicalQuizScoring from './MedicalQuizScoring';
import useMedicalQuizLogic from './useMedicalQuizLogic';
import useScrollToNextUnanswered from '../../../hooks/useScrollToNextUnanswered';
import medicalQuestions from './MedicalQuestions.json';
import { saveMedicalQuizAnswers } from '../../../api/saveMedicalQuizAnswers';
import { getCurrentUser } from 'aws-amplify/auth';
import { FaChevronRight, FaChevronLeft, FaRedo, FaExclamationTriangle, FaCheck } from 'react-icons/fa';
import './MedicalQuizStyles.css';

function MedicalQuiz({ onProgressChange }) {
  // Get quiz logic from custom hook
  const {
    currentQuestionIndex,
    selectedAnswers,
    handleAnswerSelection,
    goToNextPage,
    goToPreviousPage,
    resetQuiz,
    totalQuestions,
    currentQuestions,
    page,
    questionsPerPage,
    answeredCount,
    isQuizComplete,
  } = useMedicalQuizLogic(medicalQuestions);

  // State for saving functionality
  const [hasSaved, setHasSaved] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [authError, setAuthError] = useState(null);
  
  // State for tracking scrolling
  const [isScrolling, setIsScrolling] = useState(false);
  
  // Track which questions are in view
  const [questionsInView, setQuestionsInView] = useState({});
  
  // Track the last answered question for scroll animation
  const [lastAnsweredIndex, setLastAnsweredIndex] = useState(null);
  
  // Create refs for intersection observer
  const observerRef = useRef(null);
  const questionRefs = useRef({});

  // Use the enhanced scroll hook with lastAnsweredIndex
  const { resetScrollStatus } = useScrollToNextUnanswered(
    selectedAnswers, 
    totalQuestions, 
    isScrolling,
    lastAnsweredIndex
  );

  // Update the parent's progress bar
  useEffect(() => {
    if (onProgressChange) {
      const progressPct = totalQuestions > 0 ? (answeredCount / totalQuestions) * 100 : 0;
      onProgressChange(progressPct);
    }
  }, [answeredCount, totalQuestions, onProgressChange]);

  // Set up intersection observer for questions
  useEffect(() => {
    // Create observer for tracking which questions are in view
    const options = {
      root: null, // use viewport
      rootMargin: '-150px 0px', // offset for header and progress bar
      threshold: 0.3, // 30% of element must be visible
    };

    observerRef.current = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        const id = entry.target.id;
        const questionNumber = id.split('-')[1];
        
        setQuestionsInView(prev => ({
          ...prev,
          [questionNumber]: entry.isIntersecting
        }));
      });
    }, options);

    // Observe all question elements
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);

  // Observe current questions when they change
  useEffect(() => {
    // Clear previous observations
    if (observerRef.current) {
      Object.values(questionRefs.current).forEach(ref => {
        if (ref) observerRef.current.unobserve(ref);
      });
    }
    
    // Observe current questions
    setTimeout(() => {
      Object.entries(questionRefs.current).forEach(([id, ref]) => {
        if (ref && observerRef.current) {
          observerRef.current.observe(ref);
        }
      });
    }, 100);
  }, [currentQuestions, page]);

  // Handle smooth scrolling for pagination
  const handlePaginationClick = (direction) => {
    setIsScrolling(true);
    
    if (direction === 'next') {
      goToNextPage();
    } else {
      goToPreviousPage();
    }
    
    // Reduced timeout from 800ms to 500ms
    setTimeout(() => {
      setIsScrolling(false);
      resetScrollStatus();
    }, 500);
  };

  // Add smooth scroll configuration
  useEffect(() => {
    // Configure smooth scroll behavior
    document.documentElement.style.scrollBehavior = 'smooth';
    
    return () => {
      document.documentElement.style.scrollBehavior = '';
    };
  }, []);

  // Enhanced answer selection handler that tracks the last answered question
  const handleAnswerWithScroll = (questionIndex, value) => {
    // Only set lastAnsweredIndex if the answer is changing
    if (selectedAnswers[questionIndex] === undefined) {
      setLastAnsweredIndex(questionIndex);
    }
    
    // Call the original handler
    handleAnswerSelection(questionIndex, value);
  };

  // Handle quiz completion and save answers
  useEffect(() => {
    if (isQuizComplete && !hasSaved) {
      async function saveQuizAnswers() {
        setIsSaving(true);
        setSaveError(null);
        setAuthError(null);

        try {
          // Check if user is authenticated
          const currentUser = await getCurrentUser();
          await saveMedicalQuizAnswers(currentUser.userId, selectedAnswers);
          setHasSaved(true);
        } catch (error) {
          console.error('Error:', error);
          if (error.name === 'NotAuthorizedException' || error.name === 'UserNotAuthenticated') {
            setAuthError('Please sign in to save your answers');
          } else {
            setSaveError('Failed to save your answers. Please try again.');
          }
        } finally {
          setIsSaving(false);
        }
      }
      saveQuizAnswers();
    }
  }, [isQuizComplete, selectedAnswers, hasSaved]);

  // Render quiz completion view
  if (isQuizComplete) {
    return (
      <div className="medical-quiz-container">
        <h1>MedMatch Quiz</h1>
        <div className="quiz-completed">
          <h2>Quiz Completed!</h2>
          {isSaving && (
            <div className="saving-indicator">
              <div className="spinner"></div>
              <p>Saving your answers...</p>
            </div>
          )}
          {authError && (
            <div className="auth-error">
              <p className="error-message">
                <FaExclamationTriangle />
                <span>{authError}</span>
              </p>
              <a href="/login" className="login-link">Go to Login</a>
            </div>
          )}
          {hasSaved && (
            <div className="success-message">
              <FaCheck />
              <span>Your answers have been saved!</span>
            </div>
          )}
          <MedicalQuizScoring selectedAnswers={selectedAnswers} />
          <button 
            className="reset-quiz-button" 
            onClick={resetQuiz}
            disabled={isSaving}
          >
            <FaRedo />
            Reset Quiz
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="medical-quiz-container">
      <h1>MedMatch Quiz</h1>
      <div className="quiz-controls">
        <button className="reset-quiz-button" onClick={resetQuiz}>
          <FaRedo />
          Reset Quiz
        </button>
        <div className="quiz-info">
          <span>Question {currentQuestionIndex + 1} of {totalQuestions}</span>
        </div>
      </div>

      <div className="progress-stats">
        <span className="progress-label">Progress</span>
        <span className="progress-percentage">{Math.round((answeredCount / totalQuestions) * 100)}%</span>
      </div>
      
      <div className="questions">
        {currentQuestions.map((question, idx) => {
          const questionNumber = question.number;
          const isInView = questionsInView[questionNumber];
          
          return (
            <div
              key={questionNumber}
              id={`question-${questionNumber}`}
              ref={el => questionRefs.current[questionNumber] = el}
              className={`question-block ${isInView ? 'question-in-view' : ''}`}
            >
              <div className="question-number">{questionNumber}</div>
              <h3>{question.questionText}</h3>
              <ul>
                {question.options.map((option, optionIndex) => {
                  const inputId = `question-${questionNumber}-option-${optionIndex}`;
                  return (
                    <li key={optionIndex}>
                      <input
                        type="radio"
                        id={inputId}
                        name={`question-${questionNumber}`}
                        value={option.value}
                        checked={selectedAnswers[questionNumber - 1] === option.value}
                        onChange={() => handleAnswerWithScroll(questionNumber - 1, option.value)}
                      />
                      <label className="option-button" htmlFor={inputId}>
                        <span>{option.label}</span>
                      </label>
                    </li>
                  );
                })}
              </ul>
            </div>
          );
        })}
      </div>
      
      <div className="pagination">
        {page > 0 && (
          <button onClick={() => handlePaginationClick('prev')}>
            <FaChevronLeft className="icon-left" />
            Previous
          </button>
        )}
        {(page + 1) * questionsPerPage < totalQuestions && (
          <button onClick={() => handlePaginationClick('next')}>
            Next
            <FaChevronRight className="icon-right" />
          </button>
        )}
      </div>
    </div>
  );
}

export default MedicalQuiz;
